import type { Option } from '@/sections/shared/types/Option';

const excludeWords = ['de', 'del', 'la', 'las', 'los', 'region'];

/**
 * Sanitizes a string by converting it to lowercase, removing diacritics,
 * stripping out non-word and non-space characters, and trimming whitespace.
 *
 * @param str - The input string to sanitize.
 * @returns The sanitized string.
 */
const sanitizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s]/g, '')
    .trim();
};

const filterSignificantWords = (words: string[]): string[] => {
  return words.filter(
    (word) => word.length >= 3 && !excludeWords.includes(word)
  );
};

const hasMatchingWords = (list1: string[], list2: string[]): boolean => {
  const filteredList1 = filterSignificantWords(list1);
  const filteredList2 = filterSignificantWords(list2);

  const matchingWords = filteredList1.filter((word) =>
    filteredList2.includes(word)
  );

  if (matchingWords.length > 0) {
    return true;
  }

  const fullText1 = list1.join('').toLowerCase();
  const fullText2 = list2.join('').toLowerCase();

  return fullText1.includes(fullText2) || fullText2.includes(fullText1);
};

const validateRegionMatch = (
  selectedRegion: number,
  possibleRegion: string,
  regionOptions: Option[]
): boolean => {
  const selectedRegionLabel =
    regionOptions.find(
      (option) => Number(option.value) === Number(selectedRegion)
    )?.label || '';

  const sanitizedSelected = sanitizeString(selectedRegionLabel);
  const sanitizedPossible = sanitizeString(possibleRegion);

  return hasMatchingWords(
    sanitizedSelected.split(/\s+/),
    sanitizedPossible.split(/\s+/)
  );
};

const validateCommuneMatch = (
  selectedCommune: string,
  possibleCommune: string,
  communeOptions: Option[]
): boolean => {
  const selectedCommuneLabel =
    communeOptions.find((option) => option.value === selectedCommune)?.label ||
    '';

  const sanitizedSelected = sanitizeString(selectedCommuneLabel);
  const sanitizedPossible = sanitizeString(possibleCommune);

  return hasMatchingWords(
    sanitizedSelected.split(/\s+/),
    sanitizedPossible.split(/\s+/)
  );
};

export const validateAddressMatch = (
  values: { streetName: string; region: number; commune: string },
  placeData: { possibleRegion: string; possibleCommune: string } | null,
  regionOptions: Option[],
  communeOptions: Option[]
): boolean => {
  if (!placeData || !values.streetName || !values.region || !values.commune)
    return true;

  const regionMatches = validateRegionMatch(
    values.region,
    placeData.possibleRegion,
    regionOptions
  );

  if (!regionMatches) return false;

  return validateCommuneMatch(
    values.commune,
    placeData.possibleCommune,
    communeOptions
  );
};
